/** Job submission (PRD §16) — built to take under a minute on a phone: defaults everywhere, advanced fields folded. */
import { CreateJobRequest } from "@contentops/contract";
import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import type { z } from "zod";
import { navigate } from "../../app/router";
import { ApiError, describeError } from "../../api/errors";
import type { Category, Language } from "../../api/types";
import { Icon } from "../../components/Icon";
import { useToast } from "../../components/toast";
import { Button, Chip, Field, PageHeader, Progress, Segmented } from "../../components/ui";
import { useApi, useCache } from "../../data/hooks";
import { useCategories, useLanguages } from "../../data/queries";
import { languageName } from "../../lib/format";

type SourceType = "srt" | "youtube_url" | "topic";
type EpisodeMode = "ai_decide" | "exact" | "max";
type Upload = { status: "idle" | "uploading" | "done" | "error"; progress: number; artifactId?: string; name?: string; error?: string };

const FALLBACK_CATEGORIES: Category[] = [
  { id: "faith", slug: "faith", name: "Faith", enabled: true, default_languages: ["en"] },
  { id: "finance", slug: "finance", name: "Finance", enabled: true, default_languages: ["en"] },
  { id: "engineering", slug: "engineering", name: "Engineering", enabled: true, default_languages: ["en"] },
];
const FALLBACK_LANGUAGES: Language[] = ["en", "ur", "ar", "fa"].map((code) => ({ code, name: languageName(code), direction: code === "en" ? "ltr" : "rtl", readiness: "ready" }));

export function titleFromFilename(name: string): string {
  return name.replace(/\.[^.]+$/, "").replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim().replace(/^\w/, (c) => c.toUpperCase());
}

export function NewJobScreen() {
  const api = useApi();
  const cache = useCache();
  const toast = useToast();
  const cats = useCategories();
  const langs = useLanguages();
  const catErr = Boolean(cats.error), langErr = Boolean(langs.error);
  const categories = useMemo(() => (cats.data ?? (catErr ? FALLBACK_CATEGORIES : [])).filter((c) => c.enabled), [cats.data, catErr]);
  const languages = useMemo(() => (langs.data ?? (langErr ? FALLBACK_LANGUAGES : [])).filter((l) => l.readiness !== "disabled"), [langs.data, langErr]);

  const [sourceType, setSourceType] = useState<SourceType>("srt");
  const [upload, setUpload] = useState<Upload>({ status: "idle", progress: 0 });
  const [url, setUrl] = useState("");
  const [topic, setTopic] = useState("");
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [speaker, setSpeaker] = useState({ name: "", org: "" });
  const [mode, setMode] = useState<EpisodeMode>("ai_decide");
  const [n, setN] = useState(5);
  const [langSel, setLangSel] = useState<string[]>(["en"]);
  const [langTouched, setLangTouched] = useState(false);
  const [showAllLangs, setShowAllLangs] = useState(false);
  const [audience, setAudience] = useState("all_ages");
  const [notes, setNotes] = useState("");
  const [priority, setPriority] = useState<"normal" | "urgent">("normal");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  // First category is the default; languages follow the category until the owner changes them (PRD §16 "inherit").
  useEffect(() => { if (!category && categories[0]) setCategory(categories[0].slug); }, [categories, category]);
  useEffect(() => {
    const c = categories.find((x) => x.slug === category);
    if (!c || langTouched) return;
    const next = c.default_languages.length ? c.default_languages : ["en"];
    setLangSel((cur) => (cur.join() === next.join() ? cur : next));
  }, [category, categories, langTouched]);
  useEffect(() => () => abortRef.current?.abort(), []);

  async function pickFile(file: File | undefined) {
    if (!file) return;
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setUpload({ status: "uploading", progress: 0, name: file.name });
    if (!title.trim()) setTitle(titleFromFilename(file.name));
    setErrors((e) => ({ ...e, source: "" }));
    try {
      const r = await api.upload(file, { kind: "source_srt", signal: ctrl.signal, onProgress: (p) => setUpload((u) => ({ ...u, progress: p })) });
      setUpload({ status: "done", progress: 1, name: file.name, artifactId: r.artifact_id });
    } catch (err) {
      if (err instanceof ApiError && err.code === "aborted") return;
      setUpload({ status: "error", progress: 0, name: file.name, error: describeError(err) });
    }
  }

  function buildRequest(): z.input<typeof CreateJobRequest> | null {
    const source =
      sourceType === "srt" ? { type: "srt" as const, title: title.trim(), artifact_id: upload.artifactId ?? "" }
      : sourceType === "youtube_url" ? { type: "youtube_url" as const, title: title.trim(), url: url.trim() }
      : { type: "topic" as const, title: title.trim(), topic: topic.trim() };
    const next: Record<string, string> = {};
    if (sourceType === "srt" && upload.status !== "done") next.source = upload.status === "uploading" ? "Wait for the upload to finish." : "Choose a transcript file.";
    if (!title.trim()) next.title = "Give the job a title.";
    if (!category) next.category = "Choose a category.";
    if (!langSel.length) next.languages = "Choose at least one language.";
    if (mode !== "ai_decide" && (!Number.isInteger(n) || n < 1 || n > 30)) next.n = "Choose between 1 and 30 episodes.";
    const req = {
      source, category,
      speaker: speaker.name.trim() ? { name: speaker.name.trim(), ...(speaker.org.trim() ? { org: speaker.org.trim() } : {}) } : undefined,
      episode_policy: mode === "ai_decide" ? { mode } : { mode, n },
      languages: langSel, audience, notes: notes.trim() || undefined, priority,
    };
    const parsed = CreateJobRequest.safeParse(req);
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        const [head, sub] = issue.path.map(String);
        const key = head === "source" ? (sub === "title" ? "title" : "source") : head === "episode_policy" ? "n" : head ?? "form";
        if (!next[key]) next[key] = key === "source" && sourceType === "youtube_url" ? "Paste a full YouTube link (https://…)." : key === "source" && sourceType === "topic" ? "Describe the topic." : issue.message;
      }
    }
    setErrors(next);
    return Object.keys(next).length ? null : req;
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    const req = buildRequest();
    if (!req) {
      document.querySelector<HTMLElement>(".field-invalid input, .field-invalid textarea, .field-invalid button")?.focus();
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.createJob(req);
      cache.invalidate("series");
      cache.invalidate("dashboard");
      toast.ok("Job submitted — the Studio will pick it up shortly.");
      navigate(r.series_id ? `/studio/${r.series_id}` : "/studio");
    } catch (err) {
      if (err instanceof ApiError && err.code === "validation") setErrors({ form: err.message });
      else toast.error(err, "Couldn't submit");
    } finally {
      setSubmitting(false);
    }
  }

  const visibleLangs = showAllLangs ? languages : languages.filter((l) => langSel.includes(l.code));
  const toggleLang = (code: string) => { setLangTouched(true); setLangSel((xs) => (xs.includes(code) ? xs.filter((x) => x !== code) : [...xs, code])); };

  return (
    <form className="stack-lg form" onSubmit={submit} noValidate>
      <PageHeader title="New job" subtitle="Give the Studio a source. It reads the whole thing and proposes a series plan for you to approve." />

      <Segmented label="Source" value={sourceType} onChange={(v) => { setSourceType(v); setErrors({}); }} options={[
        { value: "srt", label: "Transcript" }, { value: "youtube_url", label: "YouTube" }, { value: "topic", label: "Topic" },
      ]} />

      {sourceType === "srt" && (
        <Field label="Transcript file" hint=".srt, .vtt or .txt — uploads as soon as you pick it" error={errors.source}>
          {(id, d) => (
            <div className="upload">
              <label htmlFor={id} className="upload-drop">
                <Icon name="upload" />
                <span>{upload.name ?? "Choose a file"}</span>
              </label>
              <input id={id} aria-describedby={d} className="sr-only" type="file" accept=".srt,.vtt,.txt,text/plain,application/x-subrip" onChange={(e) => void pickFile(e.target.files?.[0])} />
              {upload.status === "uploading" && <Progress value={upload.progress} label={`Uploading ${upload.name}`} />}
              {upload.status === "done" && <p className="ok-text"><Icon name="check" size={18} /> Uploaded</p>}
              {upload.status === "error" && <p className="field-error" role="alert">{upload.error}</p>}
            </div>
          )}
        </Field>
      )}
      {sourceType === "youtube_url" && (
        <Field label="YouTube link" hint="The Studio fetches and transcribes the video, then proposes episodes." error={errors.source}>
          {(id, d) => <input id={id} aria-describedby={d} type="url" inputMode="url" autoComplete="off" placeholder="https://www.youtube.com/watch?v=…" value={url} onChange={(e) => setUrl(e.target.value)} />}
        </Field>
      )}
      {sourceType === "topic" && (
        <>
          <Field label="Topic" hint="The Studio researches the topic with cited sources, then proposes episodes." error={errors.source}>
            {(id, d) => <textarea id={id} aria-describedby={d} rows={2} placeholder="e.g. The ethics of debt in Islamic finance" value={topic} onChange={(e) => setTopic(e.target.value)} />}
          </Field>
          <Field label="Description" optional hint="Who it's for, the angle, sources to use or avoid. Sent to the Studio as notes.">
            {(id, d) => <textarea id={id} aria-describedby={d} rows={3} placeholder="e.g. For young professionals; Shia sources first; practical examples" value={notes} onChange={(e) => setNotes(e.target.value)} />}
          </Field>
        </>
      )}

      <Field label="Title" error={errors.title}>
        {(id, d) => <input id={id} aria-describedby={d} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Du'a 'Arafah — Lecture 3" autoComplete="off" />}
      </Field>

      <div className="field">
        <span className="field-label" id="cat-label">Category</span>
        <div className="chips" role="group" aria-labelledby="cat-label">
          {categories.map((c) => <Chip key={c.slug} pressed={category === c.slug} onClick={() => { setCategory(c.slug); setLangTouched(false); }}>{c.name}</Chip>)}
          {!categories.length && <span className="muted">Loading categories…</span>}
        </div>
        {errors.category && <p className="field-error" role="alert">{errors.category}</p>}
      </div>

      <div className="field">
        <Segmented label="Episodes" value={mode} onChange={setMode} options={[
          { value: "ai_decide", label: "AI decides", hint: "Every episode worth making; you select" },
          { value: "exact", label: "Exactly" }, { value: "max", label: "At most" },
        ]} />
        {mode !== "ai_decide" && (
          <div className="stepper" role="group" aria-label="Number of episodes">
            <Button variant="secondary" aria-label="Fewer episodes" onClick={() => setN((x) => Math.max(1, x - 1))} icon="back" />
            <input aria-label="Episode count" inputMode="numeric" value={n} onChange={(e) => setN(Number(e.target.value.replace(/\D/g, "")) || 0)} />
            <Button variant="secondary" aria-label="More episodes" onClick={() => setN((x) => Math.min(30, x + 1))} icon="chevronRight" />
            <span className="muted">episodes</span>
          </div>
        )}
        {errors.n && <p className="field-error" role="alert">{errors.n}</p>}
        <p className="field-hint">{mode === "ai_decide" ? "Recommended. Your selection at plan approval is final." : mode === "exact" ? "The Studio proposes exactly this many." : "The Studio proposes up to this many, strongest hooks first."}</p>
      </div>

      <div className="field">
        <span className="field-label" id="lang-label">Languages</span>
        <div className="chips" role="group" aria-labelledby="lang-label">
          {visibleLangs.map((l) => (
            <Chip key={l.code} pressed={langSel.includes(l.code)} onClick={() => toggleLang(l.code)}>
              {l.name}{l.readiness === "piloting" && <span className="muted small"> · pilot</span>}
            </Chip>
          ))}
          <button type="button" className="text-link" onClick={() => setShowAllLangs((x) => !x)} aria-expanded={showAllLangs}>{showAllLangs ? "Done" : "Change"}</button>
        </div>
        <p className="field-hint">{langTouched ? "Custom selection." : "From the category's defaults."} The master is made in the first language; the others are derived after you approve it.</p>
        {errors.languages && <p className="field-error" role="alert">{errors.languages}</p>}
      </div>

      <Segmented label="Priority" value={priority} onChange={setPriority} options={[{ value: "normal", label: "Normal" }, { value: "urgent", label: "Urgent" }]} />

      <details className="more">
        <summary><Icon name="chevronDown" size={18} /> More options <span className="muted small">speaker, audience{sourceType === "topic" ? "" : ", notes"}</span></summary>
        <div className="stack">
          <div className="grid-2">
            <Field label="Speaker" optional>{(id) => <input id={id} value={speaker.name} onChange={(e) => setSpeaker((s) => ({ ...s, name: e.target.value }))} autoComplete="off" placeholder="Credited by name only" />}</Field>
            <Field label="Organisation" optional>{(id) => <input id={id} value={speaker.org} onChange={(e) => setSpeaker((s) => ({ ...s, org: e.target.value }))} autoComplete="off" />}</Field>
          </div>
          <Field label="Audience">
            {(id) => (
              <select id={id} value={audience} onChange={(e) => setAudience(e.target.value)}>
                <option value="all_ages">All ages (default)</option>
                <option value="children">Children</option>
                <option value="teens">Teens</option>
                <option value="adults">Adults</option>
              </select>
            )}
          </Field>
          {sourceType !== "topic" && (
            <Field label="Notes to the Studio" optional hint="Emphasis, things to avoid, a preferred angle.">
              {(id, d) => <textarea id={id} aria-describedby={d} rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />}
            </Field>
          )}
        </div>
      </details>

      {errors.form && <p className="field-error" role="alert">{errors.form}</p>}
      <div className="form-actions">
        <Button type="submit" variant="primary" size="lg" block busy={submitting} disabled={upload.status === "uploading"} icon="send">Submit to the Studio</Button>
      </div>
    </form>
  );
}
